import { FileText, ShieldCheck, Zap, BarChart3, Users, ScrollText, BookOpenCheck } from "lucide-react";

const SECTIONS = [
  {
    icon: BarChart3,
    title: "Dashboard",
    desc: "Vue synthétique des KPIs financiers, produit et opérationnels.",
    items: [
      { k: "MRR", v: "Somme des abonnements Stripe actifs, normalisée en base mensuelle. Source : Stripe API live." },
      { k: "ARR", v: "MRR × 12. Objectif : 120 000 € à 24 mois." },
      { k: "CA période", v: "Σ orders.total_eur (status = paid) sur la période. Source : MongoDB collection `orders`." },
      { k: "Conversion gratuit → payé", v: "paid_scans / free_scans × 100, sur la période sélectionnée." },
      { k: "Marge réelle estimée", v: "(CA − coûts LLM estimés − coûts infra estimés) / CA × 100. Coûts basés sur des moyennes par scan tant que l'instrumentation tokens réel n'est pas déployée." },
      { k: "Renouv. J-30 / J-60", v: "Abonnements Stripe dont current_period_end tombe dans les prochains 30 / 30-60 jours." },
      { k: "Objectifs", v: "Progression vers 270 clients Surveillance et 120 000 € ARR à 24 mois." },
    ],
  },
  {
    icon: Zap,
    title: "Funnel",
    desc: "Cascade des étapes clés — sur la période courante vs période précédente (N-1).",
    items: [
      { k: "Scans gratuits", v: "count(scans where is_paid = false) sur la période." },
      { k: "Inscriptions", v: "count(users where role != admin) créés sur la période." },
      { k: "Scans site internet payés (20 €)", v: "count(orders where type = scan, status = paid) sur la période." },
      { k: "Abonnements souscrits", v: "count(orders where type = subscription, status = paid) sur la période." },
    ],
  },
  {
    icon: ScrollText,
    title: "Journal d'audit",
    desc: "Chaque mutation effectuée depuis la console admin est journalisée.",
    items: [
      { k: "Structure", v: "id, actor_id, actor_email, actor_role, action, object_type, object_id, before, after, ip, user_agent, created_at, prev_hash, hash." },
      { k: "Immuabilité", v: "Aucun endpoint de suppression. Chaque entrée contient un hash SHA-256 chaîné (prev_hash), ce qui permet de détecter tout tampering en scannant la chaîne." },
      { k: "Vérification", v: "Bouton « Vérifier l'intégrité » : recalcule tous les hashes et signale toute rupture." },
    ],
  },
  {
    icon: ShieldCheck,
    title: "Authentification admin",
    desc: "Espace admin totalement isolé du client final.",
    items: [
      { k: "Login à deux facteurs", v: "Étape 1 : e-mail + mot de passe → Étape 2 : code à 6 chiffres envoyé par e-mail (Resend). Le code expire en 10 minutes, 5 tentatives max." },
      { k: "Session courte", v: "JWT admin (audience `empco-admin`) valide 8 h. Aucun overlap avec les tokens clients." },
      { k: "Reset par e-mail", v: "Lien signé (SHA-256 hashed en BDD, TTL 60 min, à usage unique)." },
      { k: "Blocage classique", v: "Les comptes admin ne peuvent PAS se connecter via l'API client `/api/auth/login` — 403 forcé." },
    ],
  },
  {
    icon: BookOpenCheck,
    title: "Corpus légal",
    desc: "Lexique FR/EN versionné, avec ré-évaluation automatique du parc.",
    items: [
      { k: "Publier", v: "Nouvelle version → ancienne désactivée → re-scoring de toutes les allégations → alertes clients." },
      { k: "Rollback", v: "Réactiver une version antérieure ré-applique le lexique et déclenche également la ré-évaluation." },
    ],
  },
];

export default function AdminDocs() {
  return (
    <div className="space-y-6" data-testid="admin-docs-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Console admin · Documentation</p>
        <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><FileText className="w-7 h-7 text-emerald-400" /> Guide de l'espace admin</h1>
        <p className="text-sm text-slate-400 mt-1">Description de chaque écran, formule et source de donnée. Version 1a du Lot admin.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {SECTIONS.map((s) => (
          <div key={s.title} className="bg-[#0f172a] border border-slate-800 rounded-xl p-6" data-testid={`docs-section-${s.title.slice(0,4)}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-md bg-emerald-500/10 flex items-center justify-center"><s.icon className="w-4 h-4 text-emerald-400" /></div>
              <div>
                <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                <p className="text-xs text-slate-500">{s.desc}</p>
              </div>
            </div>
            <dl className="space-y-3 mt-4">
              {s.items.map((it) => (
                <div key={it.k}>
                  <dt className="text-[11px] uppercase tracking-widest text-emerald-400 font-mono">{it.k}</dt>
                  <dd className="text-xs text-slate-300 leading-relaxed mt-1">{it.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <div className="bg-[#0b1220] border border-slate-800 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">Prochains lots</h3>
        <ul className="text-xs text-slate-400 space-y-1 list-disc pl-5">
          <li><b className="text-slate-200">Lot 1b</b> — Clients (liste paginée, fiche 360, impersonation lecture seule), Explorateur de scans (durée par étape, tokens, rejouer).</li>
          <li><b className="text-slate-200">Lot 2</b> — Argent : synchronisation Stripe complète (webhooks), réconciliation nocturne, dunning, coupons de réduction (10/20/50/100 %), factures avoirs + remboursements, export comptable mensuel.</li>
          <li><b className="text-slate-200">Lot 3</b> — Produit & Conformité : corpus versionné avancé (diff, rollback avec rapport d'impact), registre CGV planifié, RGPD (export + suppression + rétention), exploitation (feature flags, bannière maintenance, jobs).</li>
        </ul>
      </div>
    </div>
  );
}
